// M2 — 경영 통합 뷰 (pricing-corridor). /flow "통합 현황" 상단에 얹혀
// 가격 표준(최종대수 구간단가·마진)·환율 리스크·미달 clawback 을 한눈에 보여준다.
// 실적(ConsolidatedView)이 "무엇을 팔았나"라면, 여기는 "표준이 무엇이고 환율이 마진을 얼마나 먹나".
//
// 가격 모델(2026-07-16 사용자 정정): MOQ = **최종 납품 대수가 속한 구간 단가를 전량 적용**(retroactive).
// 구간 혼합 평균(prospective/blended)은 이 계약의 모델이 **아니다** → tierUnitPrice 사용.
// 리스크 = 약정 대수로 낮은 단가를 준 뒤 실인수가 미달하면 상위 구간 정가와의 차액 회수(clawback).
import { useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/db";
import { priceRepo, fxRepo, costRepo } from "@/lib/db/pricing";
import { clawbackExposure, marginPair, tierUnitPrice, type PriceTier } from "@/lib/pricing/calc";
import type { ContractRow, ContractSale, Product } from "@/types";
import { aggregateMatrix, matrixCell } from "./aggregate";
import { CountryComparisonChart, FxSensitivityChart, type FxSeries } from "./PricingCharts";

const PRODUCT_CODE = "MTX-C1";

interface Std {
  code: string;
  name: string;
  mode: "prospective" | "flat";
  tiers: PriceTier[];
  announce: number | null;
  exclusivity: string | null;
  status: string;
}

const TW_TIERS: PriceTier[] = [
  { qtyFrom: 1, qtyTo: 10, unitPriceUsd: 18000 },
  { qtyFrom: 11, qtyTo: 19, unitPriceUsd: 17000 },
  { qtyFrom: 20, qtyTo: 29, unitPriceUsd: 16000 },
  { qtyFrom: 30, qtyTo: 39, unitPriceUsd: 15000 },
  { qtyFrom: 40, qtyTo: 49, unitPriceUsd: 14000 },
  { qtyFrom: 50, qtyTo: null, unitPriceUsd: 13000 },
];
const FALLBACK: Std[] = [
  { code: "TW", name: "대만", mode: "prospective", tiers: TW_TIERS, announce: 18000, exclusivity: "독점", status: "active" },
  { code: "TH", name: "태국", mode: "flat", tiers: [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: 10000 }], announce: 18500, exclusivity: "비독점", status: "active" },
  { code: "IN", name: "인도", mode: "flat", tiers: [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: 10000 }], announce: null, exclusivity: "—", status: "draft" },
];
const COUNTRY_NAME: Record<string, string> = { TW: "대만", TH: "태국", IN: "인도", VN: "베트남" };
// MANIFEST 토큰 정합 — TW=브라스(주력·독점), TH=게인, IN=graphite-mut(draft), VN=brass-deep.
const COUNTRY_COLOR: Record<string, string> = { TW: "#c67f2c", TH: "#0e8a60", IN: "#566579", VN: "#9a5f16" };

const money = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—");
const won = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString("ko-KR") : "—");
const eok = (n: number) => (Number.isFinite(n) ? (n / 1e8).toFixed(2) + "억" : "—");
const pctS = (n: number) => (Number.isFinite(n) ? (n * 100).toFixed(1) + "%" : "—");

export default function ManagementDeck({
  contracts,
  sales,
  products,
}: {
  contracts: ContractRow[];
  sales: ContractSale[];
  products: Product[];
}): JSX.Element {
  const [stds, setStds] = useState<Std[]>(FALLBACK);
  const [fx, setFx] = useState(1500);
  const [costNet, setCostNet] = useState(6_000_000);
  const [qty, setQty] = useState(50); // 약정(최종) 대수 → 구간 단가를 결정
  const [actualQty, setActualQty] = useState(50); // 실인수 대수 (미달 시나리오) — 기본 = 약정 대수(미달 없음)
  // 실인수는 약정을 넘을 수 없다(초과분은 clawback 과 무관) → 약정 하향 시 자동 클램프.
  const actual = Math.min(actualQty, qty);

  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    (async () => {
      try {
        const loaded: Std[] = [];
        for (const code of ["TW", "TH", "IN"]) {
          const p = await priceRepo.policyFor(PRODUCT_CODE, code);
          if (p) {
            loaded.push({
              code: p.country_code,
              name: COUNTRY_NAME[p.country_code] ?? p.country_code,
              mode: p.pricing_mode,
              tiers: p.pricing_mode === "flat"
                ? [{ qtyFrom: 1, qtyTo: null, unitPriceUsd: p.flat_price_usd ?? 0 }]
                : p.tiers.map((t) => ({ qtyFrom: t.qty_from, qtyTo: t.qty_to, unitPriceUsd: t.unit_price_usd })),
              announce: p.announce_price_usd,
              exclusivity: p.exclusivity,
              status: p.status,
            });
          }
        }
        if (loaded.length) setStds(loaded);
      } catch {
        /* 폴백 유지 */
      }
      fxRepo.latest().then((r) => r && setFx(Number(r.rate))).catch(() => {});
      costRepo.latest(PRODUCT_CODE).then((c) => c && setCostNet(c.cost_krw_net)).catch(() => {});
    })();
  }, []);

  const rows = useMemo(
    () =>
      stds.map((s) => {
        // retroactive 표준 — 최종(약정) 대수가 속한 구간 단가를 전량 적용. flat 정책도 동일 함수로 처리.
        const unit = tierUnitPrice(qty, s.tiers);
        const total = qty * unit;
        const mp = marginPair(unit, fx, costNet, null);
        // 미달 clawback — 약정 qty 로 낮은 단가를 줬는데 실인수가 actual 로 미달한 경우 회수 노출.
        const claw = clawbackExposure(qty, actual, s.tiers);
        return { ...s, total, unit, margin: mp.marginPctNet, costUsd: mp.costUsdNet, claw };
      }),
    [stds, qty, actual, fx, costNet],
  );

  // 국가 간 격차 (기준 = 최저 구간단가)
  const minUnit = Math.min(...rows.map((r) => r.unit).filter((n) => n > 0), Infinity);
  // 확정 실적 = 장비×국가 교차집계 (total=전체, colTotal=국가별, count=건수 일원화)
  const mtx = useMemo(() => aggregateMatrix(sales, contracts, products, "all"), [sales, contracts, products]);
  const trigLabel = fx < 1400 ? "▼ 재협상 트리거" : fx < 1450 ? "관찰" : "정상";
  const trigFlag = fx < 1400 ? "bg-risk-tint text-risk" : fx < 1450 ? "bg-warn-tint text-warn" : "bg-gain-tint text-gain";
  // mode "prospective" 는 DB(price_policy.pricing_mode) 레거시 라벨 = "구간(tier) 정책" 의미. flat 과 구분용.
  const primary = rows.find((r) => r.mode === "prospective") ?? rows[0];
  const primaryGap = primary && minUnit > 0 && Number.isFinite(minUnit) ? (primary.unit - minUnit) / minUnit : 0;
  const totalClawback = rows.reduce((s, r) => s + (r.claw > 0 ? r.claw : 0), 0);
  const shortfall = qty - actual;

  // M2 차트 데이터 — calc 결과의 투영일 뿐(Design §1): 자체 계산 없이 marginPair 재사용.
  const FX_AXIS = useMemo(() => {
    const a: number[] = [];
    for (let f = 1300; f <= 1650; f += 25) a.push(f);
    return a;
  }, []);
  const fxSeries: FxSeries[] = useMemo(
    () =>
      rows.map((r) => ({
        name: r.name,
        color: COUNTRY_COLOR[r.code] ?? "#64748b",
        margins: FX_AXIS.map((f) => {
          const m = marginPair(r.unit, f, costNet, null).marginPctNet;
          return Number.isFinite(m) ? +(m * 100).toFixed(2) : NaN;
        }),
      })),
    [rows, FX_AXIS, costNet],
  );
  const cmp = useMemo(
    () => ({
      labels: rows.map((r) => r.name),
      unitPrice: rows.map((r) => Math.round(r.unit)),
      marginPct: rows.map((r) => (Number.isFinite(r.margin) ? +(r.margin * 100).toFixed(1) : 0)),
      gapPct: rows.map((r) => {
        const g = minUnit > 0 && Number.isFinite(minUnit) ? (r.unit - minUnit) / minUnit : 0;
        return +(g * 100).toFixed(1);
      }),
    }),
    [rows, minUnit],
  );

  return (
    <div className="mb-6 overflow-hidden rounded-lg border border-line bg-surface">
      {/* 헤더 + 컨트롤 */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-bold tracking-tight text-graphite before:h-3 before:w-[3px] before:rounded-sm before:bg-brass before:content-['']">
            가격·마진 표준 — 경영 뷰
          </h3>
          <p className="mt-1.5 text-[11px] text-graphite-mut">
            대만 Herbest v3 Appendix&nbsp;I 기준 · 최종 대수 구간 단가를 전량 적용 · 원가 KRW·매출 USD → 환율이 마진을 직접 잠식
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-5">
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-graphite-mut">
            <span>
              약정 대수 <b className="font-mono text-[13px] text-graphite">{qty}</b>
            </span>
            <input type="range" min={1} max={200} value={qty} onChange={(e) => setQty(+e.target.value)} className="w-40 accent-ink" />
          </label>
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-graphite-mut">
            <span>
              실인수 <b className="font-mono text-[13px] text-graphite">{actual}</b>
              {shortfall > 0 && (
                <em className="ml-1.5 rounded bg-risk-tint px-1.5 py-0.5 text-[10px] font-bold not-italic text-risk">미달 {shortfall}대</em>
              )}
            </span>
            <input type="range" min={1} max={qty} value={actual} onChange={(e) => setActualQty(+e.target.value)} className="w-40 accent-ink" />
          </label>
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-graphite-mut">
            <span>
              환율 <b className="font-mono text-[13px] text-graphite">₩{won(fx)}</b>{" "}
              <em className={"ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold not-italic " + trigFlag}>{trigLabel}</em>
            </span>
            <input type="range" min={1300} max={1650} step={5} value={fx} onChange={(e) => setFx(+e.target.value)} className="w-40 accent-brass" />
          </label>
        </div>
      </div>

      {/* KPI 인스트루먼트 스트립 */}
      {primary && (
        <div className="grid grid-cols-2 gap-px border-b border-line bg-line md:grid-cols-4 xl:grid-cols-5">
          <Kpi accent="hero" label={`구간 단가 @${qty}대 · ${primary.name}`} value={`$${money(primary.unit)}`} sub={`전량 적용 → 총 $${money(primary.total)}`} />
          <Kpi
            accent={primary.margin >= 0.55 ? "good" : primary.margin >= 0.4 ? "warn" : "bad"}
            tone
            label={`단가 마진율 · ${primary.name}`}
            value={pctS(primary.margin)}
            sub="순원가 기준"
          />
          <Kpi label={`국가 격차 · ${primary.name} vs 최저`} value={primaryGap > 0.001 ? "+" + pctS(primaryGap) : "기준"} sub={`최저 $${money(minUnit)}`} />
          <Kpi
            accent={totalClawback > 0 ? "bad" : undefined}
            tone={totalClawback > 0}
            label="미달 clawback"
            value={totalClawback > 0 ? "+$" + money(totalClawback) : "—"}
            sub={shortfall > 0 ? `실인수 ${actual}대 · ${shortfall}대 미달 회수` : "미달 없음"}
          />
          <Kpi label="확정 실적" value={`${mtx.total.qty}대`} sub={`₩${eok(mtx.total.revKrw)} · ${mtx.count}건`} />
        </div>
      )}

      {/* 가격 표준 테이블 */}
      <div className="overflow-x-auto px-4 pt-4">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-surface-2 text-[10px] uppercase tracking-[0.06em] text-graphite-mut">
              <th className="border-b border-line-strong px-3 py-2.5 text-left font-semibold">국가 / 파트너</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-left font-semibold">모드</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-right font-semibold">공표가 (대외)</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-right font-semibold">구간 단가 @{qty}대</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-right font-semibold">단가 마진율</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-right font-semibold">국가 격차</th>
              <th className="border-b border-line-strong px-3 py-2.5 text-right font-semibold">미달 clawback</th>
            </tr>
          </thead>
          <tbody className="font-mono tabular-nums">
            {rows.map((r) => {
              const gap = minUnit > 0 && Number.isFinite(minUnit) ? (r.unit - minUnit) / minUnit : 0;
              return (
                <tr key={r.code} className="border-b border-line last:border-0 hover:bg-surface-2">
                  <td className="px-3 py-2.5 text-left font-sans">
                    <span className="font-semibold text-graphite">{r.name}</span>
                    {r.exclusivity && r.exclusivity !== "—" && (
                      <span className={"ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium " + (r.exclusivity === "독점" ? "bg-brass-tint text-brass-deep" : "border border-line bg-surface-2 text-graphite-mut")}>
                        {r.exclusivity}
                      </span>
                    )}
                    {r.status !== "active" && <span className="ml-1.5 rounded bg-warn-tint px-1.5 py-0.5 text-[10px] font-medium text-warn">draft</span>}
                  </td>
                  <td className="px-3 py-2.5 text-left font-sans text-xs text-graphite-mut">{r.mode === "prospective" ? "MOQ 구간" : "flat"}</td>
                  <td className="px-3 py-2.5 text-right text-graphite-mut">{r.announce != null ? "$" + money(r.announce) : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-graphite">
                    ${money(r.unit)}
                    <span className="mt-0.5 block text-[10px] font-normal text-graphite-mut">총 ${money(r.total)}</span>
                  </td>
                  <td className={"px-3 py-2.5 text-right font-bold " + (r.margin >= 0.55 ? "text-gain" : r.margin >= 0.4 ? "text-warn" : "text-risk")}>{pctS(r.margin)}</td>
                  <td className={"px-3 py-2.5 text-right " + (gap > 0.001 ? "font-semibold text-brass-deep" : "text-graphite-mut")}>{gap > 0.001 ? "+" + pctS(gap) : "기준"}</td>
                  <td className={"px-3 py-2.5 text-right " + (r.claw > 0 ? "font-semibold text-risk" : "text-line-strong")}>{r.claw > 0 ? "+$" + money(r.claw) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="border-b border-line px-4 pb-4 pt-2.5 text-[11px] leading-relaxed text-graphite-mut">
        <b className="font-semibold text-graphite">구간 단가</b> = 최종 납품 대수가 속한 MOQ 구간 단가를 <b className="font-semibold text-graphite">전량에 적용</b>(50대 → 13,000 × 50 = 650,000). 구간 혼합 평균 아님.
        <b className="font-semibold text-graphite"> 미달 clawback</b> = 약정 대수로 낮은 단가를 준 뒤 실인수가 미달하면, 실인수 구간 정가와의 차액을 실인수 전량에 대해 회수해야 하는 노출.
        <b className="font-semibold text-graphite"> MOQ 절벽</b> 주의 — 49대 686,000 &gt; 50대 650,000. 환율 슬라이더로 전 행 마진이 실시간 갱신 — 세율은 어떤 값도 산출하지 않음.
      </p>

      {/* M2 시각화 — 환율 민감도 곡선 + 국가비교 막대 */}
      {primary && (
        <div className="grid gap-px border-b border-line bg-line lg:grid-cols-2">
          <div className="bg-surface px-4 py-3.5">
            <h4 className="mb-2.5 text-xs font-bold text-graphite">
              환율 민감도 <span className="font-normal text-graphite-mut">· 단가 마진율 vs 환율 · 현재 ₩{won(fx)} 표시</span>
            </h4>
            <div className="h-56">
              <FxSensitivityChart fxAxis={FX_AXIS} series={fxSeries} trigger={1400} currentFx={fx} />
            </div>
          </div>
          <div className="bg-surface px-4 py-3.5">
            <h4 className="mb-2.5 text-xs font-bold text-graphite">
              국가 비교 <span className="font-normal text-graphite-mut">· 구간 단가 @{qty}대 · 마진% (@₩{won(fx)})</span>
            </h4>
            <div className="h-56">
              <CountryComparisonChart labels={cmp.labels} unitPrice={cmp.unitPrice} marginPct={cmp.marginPct} gapPct={cmp.gapPct} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10.5px] text-graphite-mut">
              <span className="font-semibold uppercase tracking-wide">국가 격차</span>
              {cmp.labels.map((n, i) => (
                <span key={n}>
                  {n}{" "}
                  <b className={"font-mono " + ((cmp.gapPct[i] ?? 0) > 0.05 ? "font-semibold text-brass-deep" : "text-graphite-mut")}>
                    {(cmp.gapPct[i] ?? 0) > 0.05 ? "+" + (cmp.gapPct[i] ?? 0).toFixed(1) + "%" : "기준"}
                  </b>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 확정 실적 — 장비×국가 매트릭스 (경영 렌즈, Design §6) */}
      <div className="px-4 py-3.5">
        <div className="mb-2.5 flex items-center justify-between">
          <h4 className="text-xs font-bold text-graphite">확정 실적 · 장비×국가 <span className="font-normal text-graphite-mut">(contract_sales)</span></h4>
          <span className="font-mono text-[11px] text-graphite-mut">확정 {mtx.count}건 · 총 {mtx.total.qty}대</span>
        </div>
        {mtx.count === 0 ? (
          <p className="rounded-md border border-dashed border-line bg-surface-2 px-3 py-3 text-xs text-graphite-mut">
            아직 확정 실적 없음 — 딜 완료 후 원장에 확정하면 장비×국가로 여기 집계됩니다.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-surface-2 text-[10px] uppercase tracking-[0.06em] text-graphite-mut">
                  <th className="border-b border-line-strong px-3 py-2 text-left font-semibold">장비 ＼ 국가</th>
                  {mtx.countries.map((c) => (
                    <th key={c} className="border-b border-line-strong px-3 py-2 text-right font-semibold">{c}</th>
                  ))}
                  <th className="border-b border-line-strong px-3 py-2 text-right font-semibold">합계</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {mtx.products.map((p) => {
                  const rt = mtx.rowTotal.get(p);
                  return (
                    <tr key={p} className="border-b border-line hover:bg-surface-2">
                      <td className="px-3 py-2 text-left font-sans font-semibold text-graphite">{p}</td>
                      {mtx.countries.map((c) => {
                        const cell = matrixCell(mtx, p, c);
                        return (
                          <td key={c} className="px-3 py-2 text-right text-graphite">
                            {cell.qty > 0 ? (
                              <>
                                {cell.qty}대
                                <span className="mt-0.5 block text-[10px] font-normal text-graphite-mut">
                                  ₩{eok(cell.revKrw)} · {cell.revKrw > 0 ? pctS(cell.margin / cell.revKrw) : "—"}
                                </span>
                              </>
                            ) : (
                              <span className="text-line-strong">·</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right font-semibold text-graphite">
                        {rt ? rt.qty : 0}대
                        <span className="mt-0.5 block text-[10px] font-normal text-graphite-mut">₩{eok(rt ? rt.revKrw : 0)}</span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-line-strong bg-surface-2 font-semibold">
                  <td className="px-3 py-2 text-left font-sans text-graphite">합계</td>
                  {mtx.countries.map((c) => {
                    const ct = mtx.colTotal.get(c);
                    return (
                      <td key={c} className="px-3 py-2 text-right text-graphite">
                        {ct ? ct.qty : 0}대
                        <span className="mt-0.5 block text-[10px] font-normal text-graphite-mut">₩{eok(ct ? ct.revKrw : 0)}</span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-graphite">
                    {mtx.total.qty}대
                    <span className="mt-0.5 block text-[10px] font-normal text-graphite-mut">₩{eok(mtx.total.revKrw)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** KPI 인스트루먼트 타일 — 좌측 시그널 바 + 모노 수치. */
function Kpi({
  label,
  value,
  sub,
  accent,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: "hero" | "good" | "warn" | "bad";
  tone?: boolean;
}): JSX.Element {
  const bar =
    accent === "hero" ? "bg-brass" : accent === "good" ? "bg-gain" : accent === "bad" ? "bg-risk" : accent === "warn" ? "bg-warn" : "bg-line-strong";
  const valTone =
    tone && accent === "good" ? "text-gain" : tone && accent === "bad" ? "text-risk" : tone && accent === "warn" ? "text-warn" : "text-graphite";
  return (
    <div className="relative bg-surface px-3.5 py-3">
      <span className={"absolute bottom-3 left-0 top-3 w-[2px] rounded-sm " + bar} />
      <div className="pl-2.5">
        <div className="text-[10.5px] font-semibold uppercase tracking-wide text-graphite-mut">{label}</div>
        <div className={"mt-1.5 font-mono text-[21px] font-medium leading-none tracking-tight tabular-nums " + valTone}>{value}</div>
        {sub && <div className="mt-1 font-mono text-[11px] text-graphite-mut">{sub}</div>}
      </div>
    </div>
  );
}
